// Feature toggles shared by client and server: switches that are on for
// single users only.
//
// A flag is on for the users who list it in `featureOverrides`, a string array
// on their `_User` that only the master key can write (the dashboard, or a
// master-key script). There is no way to switch a flag on for everyone.
//
// Delete a flag from this list once nothing depends on it any more: the
// compiler then points at every stale use.
export const FEATURE_FLAGS = [
  // The user has no second factor and is never asked for one: the one they
  // had set up is removed once they are exempt, so their login asks for no
  // code, the server does not refuse them for lacking one, and the app does
  // not send them to the enrolment screen.
  "mfaExempt",
  // The user gets the views made for debugging the calculation: the decision
  // tree of a case, generated on request, and the graphs of the ledger behind
  // an outcome. The server refuses the decision tree to anyone else.
  "decisionTree",
] as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[number];

export type FeatureFlags = Record<FeatureFlag, boolean>;

export const isFeatureFlag = (value: unknown): value is FeatureFlag =>
  FEATURE_FLAGS.includes(value as FeatureFlag);

// every flag off: what a user without overrides has
export const noFeatures = (): FeatureFlags =>
  Object.fromEntries(FEATURE_FLAGS.map((f) => [f, false])) as FeatureFlags;

// The flags of a user, from the `featureOverrides` stored on them. Anything
// but a list, and any name in it that is not a flag, is ignored, so a typo in
// the dashboard fails closed.
export function resolveFeatureFlags(overrides: unknown): FeatureFlags {
  const enabledForUser = new Set(
    Array.isArray(overrides) ? overrides.filter(isFeatureFlag) : [],
  );
  return Object.fromEntries(
    FEATURE_FLAGS.map((flag) => [flag, enabledForUser.has(flag)]),
  ) as FeatureFlags;
}
